import React, { useState, useEffect, useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

/**
 * Modern Outlined Time Picker Field with seamless auto-advancement:
 * - Uses native Material-UI Outlined TextField for 100% identical border, notch, and focus behavior.
 * - Tight spacing between hours, colon, and minutes (no excessive gap).
 * - Typing hour auto-advances to minute.
 * - Typing minute auto-advances directly to AM/PM.
 * - Outputs standard 24-hour 'HH:mm' string for DB and form handlers.
 */
const TimePickerField = ({
    label = 'Time',
    name = 'time',
    value = '',
    onChange,
    error = false,
    helperText = '',
    disabled = false,
    required = false,
    defaultPeriod = 'PM'
}) => {
    const [hours, setHours] = useState('');
    const [minutes, setMinutes] = useState('');
    const [period, setPeriod] = useState(defaultPeriod);
    const [isFocused, setIsFocused] = useState(false);

    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const ampmRef = useRef(null);
    const isInternalChange = useRef(false);

    // Parse incoming 'HH:mm' (24-hour) value into 12-hour format
    useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }

        if (value && typeof value === 'string' && value.includes(':')) {
            let timeStr = value.trim();
            // Handle full datetime strings (e.g. "2026-09-17 14:39:00" or "09/17/2026 02:39 PM")
            if (timeStr.includes(' ') || timeStr.includes('T')) {
                const spaceParts = timeStr.replace('T', ' ').split(' ').filter(Boolean);
                const timeToken = spaceParts.find((p) => p.includes(':')) || spaceParts[1] || '';
                const ampmToken = spaceParts.find((p) => p.toUpperCase() === 'AM' || p.toUpperCase() === 'PM');
                timeStr = timeToken + (ampmToken ? ` ${ampmToken}` : '');
            }

            const hasAmPm = /([AaPp][Mm])/.test(timeStr);
            const isPM = /[Pp][Mm]/.test(timeStr);
            const cleanTime = timeStr.replace(/[^\d:]/g, '');
            const parts = cleanTime.split(':');
            let h = parseInt(parts[0], 10);
            const m = parts[1] ? parts[1].slice(0, 2) : '00';

            if (!isNaN(h)) {
                let p = defaultPeriod;
                if (hasAmPm) {
                    p = isPM ? 'PM' : 'AM';
                    if (h === 0) h = 12;
                } else {
                    p = h >= 12 ? 'PM' : 'AM';
                    if (h === 0) h = 12;
                    else if (h > 12) h -= 12;
                }

                setHours(String(h).padStart(2, '0'));
                setMinutes(m.padStart(2, '0'));
                setPeriod(p);
            }
        } else if (!value) {
            setHours('');
            setMinutes('');
            setPeriod(defaultPeriod);
        }
    }, [value, defaultPeriod]);

    // Emit 24-hour formatted value to parent onChange
    const emitChange = (newHours, newMinutes, newPeriod) => {
        if (!newHours || !newMinutes || !newPeriod) {
            if (!newHours && !newMinutes) {
                isInternalChange.current = true;
                if (onChange) {
                    onChange({ target: { name, value: '' } });
                }
            }
            return;
        }

        let h = parseInt(newHours, 10);
        const m = newMinutes.padStart(2, '0');

        if (isNaN(h)) return;

        if (newPeriod === 'PM' && h < 12) {
            h += 12;
        } else if (newPeriod === 'AM' && h === 12) {
            h = 0;
        }

        const formatted24h = `${String(h).padStart(2, '0')}:${m}`;
        isInternalChange.current = true;
        if (onChange) {
            onChange({ target: { name, value: formatted24h } });
        }
    };

    // Handle Hour keystrokes and auto-advance
    const handleHourKeyDown = (e) => {
        if (disabled) return;

        if (['Tab', 'ArrowLeft', 'Delete'].includes(e.key)) return;

        if (e.key === 'ArrowRight' || e.key === ':') {
            e.preventDefault();
            if (hours.length === 1) {
                const padded = `0${hours}`;
                setHours(padded);
                emitChange(padded, minutes, period);
            }
            if (minuteRef.current) {
                minuteRef.current.focus();
                minuteRef.current.select();
            }
            return;
        }

        if (e.key === 'Backspace') {
            if (hours.length <= 1) {
                setHours('');
                emitChange('', minutes, period);
            }
            return;
        }

        // Numeric key handling
        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();
            const num = parseInt(e.key, 10);

            const isSelected = window.getSelection() && window.getSelection().toString() === hours;
            const current = (hours.length >= 2 || isSelected) ? '' : hours;

            if (current === '') {
                if (num >= 2 && num <= 9) {
                    const hVal = `0${num}`;
                    setHours(hVal);
                    emitChange(hVal, minutes, period);
                    setTimeout(() => {
                        if (minuteRef.current) {
                            minuteRef.current.focus();
                            minuteRef.current.select();
                        }
                    }, 10);
                } else {
                    setHours(String(num));
                }
            } else if (current === '0') {
                if (num >= 1 && num <= 9) {
                    const hVal = `0${num}`;
                    setHours(hVal);
                    emitChange(hVal, minutes, period);
                    setTimeout(() => {
                        if (minuteRef.current) {
                            minuteRef.current.focus();
                            minuteRef.current.select();
                        }
                    }, 10);
                }
            } else if (current === '1') {
                if (num >= 0 && num <= 2) {
                    const hVal = `1${num}`;
                    setHours(hVal);
                    emitChange(hVal, minutes, period);
                    setTimeout(() => {
                        if (minuteRef.current) {
                            minuteRef.current.focus();
                            minuteRef.current.select();
                        }
                    }, 10);
                } else {
                    const hVal = '01';
                    const mVal = `0${num}`;
                    setHours(hVal);
                    setMinutes(mVal);
                    emitChange(hVal, mVal, period);
                    setTimeout(() => {
                        if (ampmRef.current) {
                            ampmRef.current.focus();
                        }
                    }, 10);
                }
            }
        }
    };

    // Handle Minute keystrokes and auto-advance to AM/PM
    const handleMinuteKeyDown = (e) => {
        if (disabled) return;

        if (['Tab', 'Delete'].includes(e.key)) return;

        if (e.key === 'ArrowLeft') {
            if (minuteRef.current && minuteRef.current.selectionStart === 0) {
                e.preventDefault();
                if (hourRef.current) {
                    hourRef.current.focus();
                    hourRef.current.select();
                }
            }
            return;
        }

        if (e.key === 'ArrowRight' || e.key === ':') {
            e.preventDefault();
            if (minutes.length === 1) {
                const padded = `0${minutes}`;
                setMinutes(padded);
                emitChange(hours, padded, period);
            }
            if (ampmRef.current) {
                ampmRef.current.focus();
            }
            return;
        }

        if (e.key === 'Backspace') {
            if (minutes === '' || minutes.length === 0) {
                e.preventDefault();
                if (hourRef.current) {
                    hourRef.current.focus();
                    hourRef.current.select();
                }
            } else if (minutes.length === 1) {
                setMinutes('');
                emitChange(hours, '', period);
            }
            return;
        }

        if (e.key === 'a' || e.key === 'A') {
            e.preventDefault();
            const padded = minutes.length === 1 ? `0${minutes}` : (minutes || '00');
            setMinutes(padded);
            setPeriod('AM');
            emitChange(hours || '12', padded, 'AM');
            setTimeout(() => {
                if (ampmRef.current) ampmRef.current.focus();
            }, 10);
            return;
        }

        if (e.key === 'p' || e.key === 'P') {
            e.preventDefault();
            const padded = minutes.length === 1 ? `0${minutes}` : (minutes || '00');
            setMinutes(padded);
            setPeriod('PM');
            emitChange(hours || '12', padded, 'PM');
            setTimeout(() => {
                if (ampmRef.current) ampmRef.current.focus();
            }, 10);
            return;
        }

        if (/^[0-9]$/.test(e.key)) {
            e.preventDefault();
            const num = parseInt(e.key, 10);

            const isSelected = window.getSelection() && window.getSelection().toString() === minutes;
            const current = (minutes.length >= 2 || isSelected) ? '' : minutes;

            if (current === '') {
                if (num >= 6 && num <= 9) {
                    const mVal = `0${num}`;
                    setMinutes(mVal);
                    emitChange(hours, mVal, period);
                    setTimeout(() => {
                        if (ampmRef.current) ampmRef.current.focus();
                    }, 10);
                } else {
                    setMinutes(String(num));
                }
            } else {
                const mVal = `${current}${num}`;
                setMinutes(mVal);
                emitChange(hours, mVal, period);
                setTimeout(() => {
                    if (ampmRef.current) {
                        ampmRef.current.focus();
                    }
                }, 10);
            }
        }
    };

    // Handle AM/PM segment keystrokes
    const handleAmPmKeyDown = (e) => {
        if (disabled) return;

        if (e.key === 'Tab') return;

        if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
            e.preventDefault();
            if (minuteRef.current) {
                minuteRef.current.focus();
                minuteRef.current.select();
            }
            return;
        }

        if (e.key === 'a' || e.key === 'A') {
            e.preventDefault();
            setPeriod('AM');
            emitChange(hours, minutes, 'AM');
            return;
        }

        if (e.key === 'p' || e.key === 'P') {
            e.preventDefault();
            setPeriod('PM');
            emitChange(hours, minutes, 'PM');
            return;
        }

        if (['ArrowUp', 'ArrowDown', ' ', 'Enter'].includes(e.key)) {
            e.preventDefault();
            const nextPeriod = period === 'AM' ? 'PM' : 'AM';
            setPeriod(nextPeriod);
            emitChange(hours, minutes, nextPeriod);
        }
    };

    const togglePeriod = (targetPeriod) => {
        if (disabled) return;
        const p = targetPeriod || (period === 'AM' ? 'PM' : 'AM');
        setPeriod(p);
        emitChange(hours, minutes, p);
        if (ampmRef.current) {
            ampmRef.current.focus();
        }
    };

    return (
        <TextField
            fullWidth
            variant="outlined"
            label={label}
            required={required}
            error={Boolean(error)}
            helperText={helperText}
            focused={isFocused}
            disabled={disabled}
            onClick={() => {
                if (!disabled && !hours && hourRef.current) {
                    hourRef.current.focus();
                }
            }}
            InputLabelProps={{
                shrink: true
            }}
            InputProps={{
                style: {
                    height: '56px',
                    minHeight: '56px',
                    padding: '0 14px',
                    cursor: disabled ? 'not-allowed' : 'text'
                },
                startAdornment: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '1rem',
                            fontFamily: 'inherit',
                            lineHeight: 1
                        }}
                    >
                        {/* Hour Segment (snug 18px width) */}
                        <input
                            ref={hourRef}
                            type="text"
                            inputMode="numeric"
                            placeholder="--"
                            value={hours}
                            disabled={disabled}
                            className="tm-time-digit-input"
                            onFocus={() => {
                                setIsFocused(true);
                                if (hourRef.current) hourRef.current.select();
                            }}
                            onBlur={() => {
                                setIsFocused(false);
                                if (hours.length === 1) {
                                    const padded = `0${hours}`;
                                    setHours(padded);
                                    emitChange(padded, minutes, period);
                                }
                            }}
                            onKeyDown={handleHourKeyDown}
                            onChange={() => {}}
                        />

                        {/* Colon with tight padding so hour and min sit close together */}
                        <span className="tm-time-colon">
                            :
                        </span>

                        {/* Minute Segment (snug 18px width) */}
                        <input
                            ref={minuteRef}
                            type="text"
                            inputMode="numeric"
                            placeholder="--"
                            value={minutes}
                            disabled={disabled}
                            className="tm-time-digit-input"
                            onFocus={() => {
                                setIsFocused(true);
                                if (minuteRef.current) minuteRef.current.select();
                            }}
                            onBlur={() => {
                                setIsFocused(false);
                                if (minutes.length === 1) {
                                    const padded = `0${minutes}`;
                                    setMinutes(padded);
                                    emitChange(hours, padded, period);
                                }
                            }}
                            onKeyDown={handleMinuteKeyDown}
                            onChange={() => {}}
                        />

                        {/* AM / PM Toggle Pill */}
                        <div
                            ref={ampmRef}
                            tabIndex={disabled ? -1 : 0}
                            className="tm-time-ampm-pill"
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            onKeyDown={handleAmPmKeyDown}
                            style={{
                                border: isFocused ? '1px solid #93c5fd' : undefined,
                                cursor: disabled ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <span
                                className={`tm-time-ampm-btn ${period === 'AM' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePeriod('AM');
                                }}
                            >
                                AM
                            </span>
                            <span
                                className={`tm-time-ampm-btn ${period === 'PM' ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    togglePeriod('PM');
                                }}
                            >
                                PM
                            </span>
                        </div>
                    </div>
                ),
                endAdornment: (
                    <InputAdornment position="end" style={{ marginLeft: 'auto' }}>
                        <AccessTimeIcon
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hourRef.current && !disabled) {
                                    hourRef.current.focus();
                                    hourRef.current.select();
                                }
                            }}
                            style={{
                                fontSize: 20,
                                color: isFocused ? '#036dda' : '#94a3b8',
                                cursor: disabled ? 'not-allowed' : 'pointer'
                            }}
                        />
                    </InputAdornment>
                ),
                inputProps: {
                    readOnly: true,
                    tabIndex: -1,
                    style: {
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        cursor: 'default',
                        padding: 0
                    }
                }
            }}
        />
    );
};

export default TimePickerField;
